import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  ArrowRight,
  Clock,
  Users,
  CheckCircle,
  Zap,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import brochure from "../assets/GJ SkillBay Labs .pdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CTA = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contentData, setContentData] = useState(null);

  // Fetch dynamic content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content`);
        setContentData(response.data);
      } catch (error) {
        console.error("Error fetching content:", error);
        setContentData({
          stats: {
            learners_trained: "100+",
            hiring_partners: "150+",
            average_ctc: "6.2",
            placement_rate: "87%",
          },
          urgency: {
            next_cohort_date: "February 15, 2026",
            seats_available: 20,
            application_deadline: "February 10, 2026",
          },
        });
      }
    };

    fetchContent();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/newsletter`, {
        email: email,
      });

      if (response.data.success) {
        setSubmitted(true);
        setEmail("");
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(response.data.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Newsletter submission error:", error);
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Failed to subscribe. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          {/* Main CTA Card */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20 text-white">
            <CardContent className="p-12">
              <div className="text-center space-y-8">
                {/* Urgency Header */}
                <div className="space-y-4">
                  <Badge className="bg-orange-500 text-white hover:bg-orange-600 px-4 py-2 text-lg">
                    <Clock size={16} className="mr-2" />
                    Limited Seats Available
                  </Badge>

                  <h2 className="text-4xl lg:text-6xl font-bold leading-tight">
                    Download the Program Prospectus & Fee Structure
                  </h2>
                </div>

                {/* Value Props */}
                <div className="grid md:grid-cols-3 gap-11 ml-20 my-8 ">
                  <div className="flex items-center gap-3 text-left">
                    <CheckCircle
                      className="text-green-400 flex-shrink-0"
                      size={24}
                    />
                    <div>
                      <div className="font-semibold">
                        Industry-Aligned Curriculum
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-left">
                    <CheckCircle
                      className="text-green-400 flex-shrink-0"
                      size={24}
                    />
                    <div>
                      <div className="font-semibold">Transparent Pricing</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-left">
                    <CheckCircle
                      className="text-green-400 flex-shrink-0"
                      size={24}
                    />
                    <div>
                      <div className="font-semibold">Career Outcomes</div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                {!submitted ? (
                  <form
                    onSubmit={handleSubmit}
                    className="max-w-md mx-auto space-y-4"
                  >
                    {/* Error Message */}
                    {error && (
                      <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-3 flex items-center gap-2 text-red-200">
                        <AlertCircle size={16} />
                        <span className="text-sm">{error}</span>
                      </div>
                    )}

                    <div className="w-full flex items-center justify-center">
                      {/* <Button
                        type="button" // important: not submit
                        size="lg"
                        className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 disabled:opacity-50"
                        disabled={isLoading}
                        onClick={handleDownload}
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Get Full Brochure
                            <ArrowRight className="ml-2" size={20} />
                          </>
                        )}
                      </Button> */}
                      <a
                        href="#hero-container"
                        className="inline-flex items-center justify-center bg-white text-blue-600 hover:bg-blue-50 font-semibold px-6 py-3 rounded-md"
                      >
                        Get Full Brochure
                        <ArrowRight className="ml-2" size={20} />
                      </a>
                    </div>
                  </form>
                ) : (
                  <div className="max-w-md mx-auto text-center space-y-4">
                    <CheckCircle className="mx-auto text-green-400" size={48} />
                    <h3 className="text-xl font-semibold">
                      PDF Sent Successfully!
                    </h3>
                    <p className="text-blue-200">
                      Check your email and WhatsApp for the complete syllabus
                      and fees breakdown.
                    </p>
                  </div>
                )}

                {/* Risk Reversal */}

                {/* Alternative Actions */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Button
                    variant="outline"
                    size="lg"
                    className="relative z-50 bg-transparent border-white/30 text-white px-6 pointer-events-auto"
                    onClick={() => {
                      const phone = "919895223307";
                      const message =
                        "Hi, I would like to receive the syllabus via WhatsApp.";
                      const url = `https://wa.me/${phone}?text=${encodeURIComponent(
                        message
                      )}`;
                      window.location.href = url;
                    }}
                  >
                    Receive via WhatsApp
                  </Button>

                  <Button
                    variant="outline"
                    size="lg"
                    className="bg-transparent border-white/30 text-white px-6"
                    onClick={() =>
                      document
                        .getElementById("hero")
                        ?.scrollIntoView({ behavior: "smooth" })
                    }
                  >
                    Schedule a Career Consultation
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
